/**
 * Cloudinary Upload Utility
 * Handles image uploads to Cloudinary CDN for permanent storage
 */
import api from './api';

/**
 * Check if Cloudinary is configured on the backend
 */
export async function isCloudinaryConfigured() {
  try {
    const response = await api.get('/cloudinary/status');
    return response.data.configured === true;
  } catch (error) {
    return false;
  }
}

/**
 * Upload image to Cloudinary via backend
 * Falls back to local upload if Cloudinary is not configured
 * 
 * @param {File} file - The image file to upload
 * @param {string} folder - Optional folder path in Cloudinary
 * @returns {Promise<{success: boolean, url: string, isCloud: boolean}>}
 */
export async function uploadImage(file, folder = 'marissa_jewelry') {
  // First check if Cloudinary is configured
  const cloudinaryEnabled = await isCloudinaryConfigured();
  
  if (cloudinaryEnabled) {
    // Upload to Cloudinary
    return uploadToCloudinary(file, folder);
  } else {
    // Fall back to local upload
    return uploadToLocal(file);
  }
}

/**
 * Upload directly to Cloudinary (when configured)
 */
async function uploadToCloudinary(file, folder) {
  try {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await api.post(`/cloudinary/upload?folder=${folder}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    
    if (response.data.success) {
      return {
        success: true,
        url: response.data.url,
        publicId: response.data.public_id,
        isCloud: true
      };
    }
    
    throw new Error('Upload failed');
  } catch (error) {
    throw error;
  }
}

/**
 * Upload to local storage (fallback)
 */
async function uploadToLocal(file) {
  try {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await api.post('/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    
    if (response.data.success) {
      const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
      const fullUrl = `${baseUrl}${response.data.url}`;
      return {
        success: true,
        url: fullUrl,
        isCloud: false,
        warning: 'Using local storage. Images will be lost on deployment. Configure Cloudinary for permanent storage.'
      };
    }
    
    throw new Error('Upload failed');
  } catch (error) {
    throw error;
  }
}

/**
 * Upload with automatic detection and toast feedback
 * Use this in components for user-friendly uploads
 */
export async function uploadWithFeedback(file, folder, toast) {
  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
  
  if (!allowedTypes.includes(file.type)) {
    toast?.error('Please select a valid image file (JPG, PNG, GIF, WEBP)');
    throw new Error('Invalid file type');
  }
  
  // 10MB max for Cloudinary, 5MB for local
  const maxSize = 10 * 1024 * 1024;
  if (file.size > maxSize) {
    toast?.error('File size must be less than 10MB');
    throw new Error('File too large');
  }
  
  try {
    const result = await uploadImage(file, folder);
    
    if (result.warning) {
      toast?.warning(result.warning, { duration: 5000 });
    } else {
      toast?.success('Image uploaded to cloud storage');
    }
    
    return result;
  } catch (error) {
    toast?.error('Failed to upload image');
    throw error;
  }
}
